import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { Box, Divider, TABLET } from 'chemistry'
import { AllFiltersTopButton, FILTERS_TOP_ID, FilterTopDropdown, FilterTopDropdownButton } from './FilterTopButton'
import { ActiveFiltersTop } from './ActiveFiltersTop'
import { isFlagInGA, pushAllFiltersEvent, pushFlagToDatalayer } from '../../../../../common/gtmEvents'
import { handleClickStickyTopFiltersEvent } from 'client/fragments/listerFragment/store/middleware/gtm'
import { CLICK_STICKY_TOP_FILTERS } from 'client/common/analytic/analytic'
import { SHOW_NEW_AVAILABILITY_FILTER_LABELS, SHOW_STICKY_TOP_FILTERS } from 'common/constants'
import { BRICO } from 'client/common/utils'
import { useSelector } from 'react-redux'
import { dictionary } from 'client/common/accessability'
import AvailabilitySwitcher from 'client/fragments/listerFragment/components/AvailabilitySwitcher/AvailabilitySwitcher'

const FILTER_BUTTON_SELECTOR_PREFIX = 'filter_top'
const ALL_FILTERS_BUTTON_SELECTOR = 'all_filters_button'
const FILTERS_TOP_PADDING_TOP = 24

export const getFilterTopButtonSelector = index => `${FILTER_BUTTON_SELECTOR_PREFIX}_${index}`

const FLEX_GAP_SIZE = 12
const FiltersTopFlex = forwardRef((props, ref) => {
  // <Flex /> from chemistry is not forwarding ref
  return (
    <Box ref={ref} display='flex' justifyContent='flex-start' flexWrap='wrap' gap={`${FLEX_GAP_SIZE}px`} {...props} />
  )
})
const MINIMAL_NUMBER_OF_TOP_FILTERS = 1
const MAXIMUM_NUMBER_OF_TOP_FILTERS = 15

export function calculateNumberOfElementsFitting({ availableWidth, collection, letterSize }) {
  let totalWidth = 0
  let numberOfElementsFitting = 0
  for (const filter of collection) {
    const currentWidth = filter.title.length * letterSize + 50 + FLEX_GAP_SIZE
    // eslint-disable-next-line no-console
    if (totalWidth + currentWidth > availableWidth) {
      return numberOfElementsFitting
    }
    totalWidth += currentWidth
    numberOfElementsFitting++
  }
  return numberOfElementsFitting
}

export function FiltersTop({
  filters: { collection, active },
  filterProcessedData,
  filtersToggled,
  setFiltersToggled,
  allFiltersText,
  showStickyTopFilters,
  flagsToGA,
  categoryCode,
  lang,
  showAvailabilityStatusToggle
}) {
  const filtersTopRef = useRef()
  const format = useSelector(store => store.data?.format)
  const [numberOfVisibleFilters, setNumberOfVisibleFilters] = useState(MINIMAL_NUMBER_OF_TOP_FILTERS)
  const [visibleFilters, setVisibleFilters] = useState(collection.slice(0, MINIMAL_NUMBER_OF_TOP_FILTERS))
  const [hiddenFilters, setHiddenFilters] = useState(
    collection.slice(MINIMAL_NUMBER_OF_TOP_FILTERS, MAXIMUM_NUMBER_OF_TOP_FILTERS)
  )
  const [isVisible, setIsVisible] = useState(true)

  const recalculate = useCallback(() => {
    const filtersWidth = filtersTopRef?.current?.offsetWidth
    const allFiltersButtonDomElement = document.getElementById(ALL_FILTERS_BUTTON_SELECTOR)
    const allFiltersButtonDomElementWidth = allFiltersButtonDomElement?.offsetWidth ?? 0
    const availableWidth = Math.floor((filtersWidth - allFiltersButtonDomElementWidth) * 0.98)
    const numberOfElementsFitting = calculateNumberOfElementsFitting({
      availableWidth,
      collection,
      letterSize: showStickyTopFilters ? 9 : 7
    })
    const numberOfVisibleFilters = Math.max(numberOfElementsFitting, MINIMAL_NUMBER_OF_TOP_FILTERS)

    setVisibleFilters(collection.slice(0, numberOfVisibleFilters))
    setHiddenFilters(collection.slice(numberOfVisibleFilters, MAXIMUM_NUMBER_OF_TOP_FILTERS))
    setNumberOfVisibleFilters(numberOfVisibleFilters)
  }, [collection, showStickyTopFilters])

  useEffect(() => {
    if (!filtersTopRef.current) return
    window?.addEventListener('resize', recalculate)
    return () => {
      window?.removeEventListener('resize', recalculate)
    }
  }, [filtersTopRef, recalculate])

  useEffect(recalculate, [recalculate, collection, allFiltersText])

  useEffect(() => {
    if (!showStickyTopFilters) return
    const listenToScroll = () => {
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop
      const heightToTop = categoryCode ? 350 : 320
      if (winScroll > heightToTop + 25) {
        isVisible && // to limit setting state only the first time
          setIsVisible(false)
      }
      if (winScroll < heightToTop - 50) {
        setIsVisible(true)
      }
    }
    window.addEventListener('scroll', listenToScroll)
    return () => window.removeEventListener('scroll', listenToScroll)
  }, [isVisible, categoryCode, showStickyTopFilters])

  const onClickStickyFilter = ({ action }) => {
    if (isFlagInGA({ flagsToGA, ldFlag: SHOW_STICKY_TOP_FILTERS }) && showStickyTopFilters && !isVisible) {
      handleClickStickyTopFiltersEvent({
        eventAction: CLICK_STICKY_TOP_FILTERS,
        eventLabel: `sticky_filters_click_${action}`
      })
    }
  }

  const oldVersionStyles = {
    pt: `${FILTERS_TOP_PADDING_TOP}px`,
    pb: '24px',
    [TABLET]: {
      display: 'none'
    }
  }

  const newVersionStyles = {
    pt: `${FILTERS_TOP_PADDING_TOP}px`,
    pb: 0,
    [TABLET]: {
      display: 'none'
    },
    position: 'sticky',
    top: 0,
    background: 'white',
    zIndex: 1020
  }
  const topButtonNewVersionStyles = {
    border: '1px solid rgb(65,63,55)',
    bgColor: 'grey.50',
    fontWeight: '600'
  }

  return (
    <Box id={FILTERS_TOP_ID} sx={showStickyTopFilters ? newVersionStyles : oldVersionStyles}>
      <FiltersTopFlex ref={filtersTopRef} position='relative' zIndex={1020}>
        {visibleFilters.map((filter, index) => (
          <FilterTopDropdown
            key={filter.name}
            id={getFilterTopButtonSelector(index)}
            filter={filter}
            active={active}
            onClickStickyFilter={onClickStickyFilter}
            filterProcessedData={filterProcessedData}
            maxDropdownHeight={'25rem'} // approx height of 8.5 items
            upperCaseTitle={showStickyTopFilters}
          />
        ))}
        <AllFiltersTopButton
          id={ALL_FILTERS_BUTTON_SELECTOR}
          isShown={visibleFilters?.length > 0}
          onClick={() => {
            setFiltersToggled(true)
            pushAllFiltersEvent('open')
            if (
              format === BRICO &&
              isFlagInGA({ flagsToGA, ldFlag: SHOW_NEW_AVAILABILITY_FILTER_LABELS }) &&
              !filtersToggled
            ) {
              pushFlagToDatalayer({
                ldFlag: 'availability-filter-labels',
                ldVariation: 'shown'
              })
            }
          }}
          newVersionStyles={showStickyTopFilters ? topButtonNewVersionStyles : {}}
          aria-label={dictionary(lang).filtersDescription}
        >
          {showStickyTopFilters ? allFiltersText.toUpperCase() : allFiltersText}
        </AllFiltersTopButton>
        {/* All buttons have to be present in DOM. In case of resize it's possible to calculate which ones can fit in one line from their width */}
        {hiddenFilters.map((filter, index) => (
          <FilterTopDropdownButton
            key={filter.name}
            id={getFilterTopButtonSelector(index + numberOfVisibleFilters)}
            filter={filter}
            visibility='hidden'
            position='absolute'
          />
        ))}
      </FiltersTopFlex>
      {showAvailabilityStatusToggle && (
        <AvailabilitySwitcher
          lang={lang}
          format={format}
          collection={collection}
          filterProcessedData={filterProcessedData}
        />
      )}
      <Divider width='100%' m='24px 0' />
      {isVisible && (
        <ActiveFiltersTop
          activeFilters={active}
          onRemove={filterProcessedData.onChange}
          onClear={filterProcessedData.onClear}
        />
      )}
    </Box>
  )
}
